/**
 * FinanceReportSection — "דוח כספים" tab of the unified Reports page.
 *
 * All analytics come from the live server-maintained monthly report doc
 * (schema v3); only the transactions table fetches raw rows via
 * useGetTransactionsInRangeQuery.
 *
 * Design modeled on FinanceReportsPage (KPI row, cumulative line, doughnuts,
 * month comparison bars, status pie, sortable table + CSV export).
 *
 * Error codes: CK-3711 (CSV export).
 */

import { useCallback, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  Wallet,
  Receipt,
  ChartLineUp,
  Percent,
  HourglassMedium,
  TrendUp,
  TrendDown,
  Warning,
  UserCircle,
  Export,
  SortAscending,
  SortDescending,
} from "@phosphor-icons/react";
import { useGetTransactionsInRangeQuery } from "../../../store/services/financeApi";
import {
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHOD_COLORS,
} from "../../../constants/paymentMethods";
import ContentLoader from "../../shared/ContentLoader";
import "./FinanceReportSection.css";

// Copied from FinanceReportsPage (that page is being retired)
const STATUS_LABELS = {
  paid: "שולם",
  pending: "ממתין לתשלום",
  pending_collection: "ממתין לתשלום",
  expected: "צפוי",
  invoice_manual: "חשבונית (ידני)",
  invoice_auto: "חשבונית (אוטומטי)",
  refunded: "זיכוי",
  cancelled: "בוטל",
  split: "פוצל",
};

const STATUS_COLORS = {
  paid: "#22c55e",
  pending: "#f59e0b",
  pending_collection: "#f59e0b",
  expected: "#3b82f6",
  invoice_manual: "#8b5cf6",
  invoice_auto: "#a78bfa",
  refunded: "#ef4444",
  cancelled: "#9ca3af",
  split: "#06b6d4",
};

const PATIENT_COLORS = [
  "#4dd9b4", "#3b82f6", "#8b5cf6", "#f59e0b", "#ec4899",
  "#22c55e", "#ef4444", "#06b6d4", "#f97316", "#84cc16",
];

const NEUTRAL_GRAY = "#9ca3af";

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "₪0";
  return `₪${Math.round(amount).toLocaleString("he-IL")}`;
};

const toDate = (v) => {
  if (!v) return null;
  if (v.seconds) return new Date(v.seconds * 1000);
  return new Date(v);
};

const formatTxDate = (v) => {
  const d = toDate(v);
  if (!d || Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Asia/Jerusalem",
  });
};

const KpiCard = ({ icon, label, value, tone }) => (
  <div className={`frs-kpi-card ${tone ? `frs-kpi-card--${tone}` : ""}`}>
    {icon}
    <span className="frs-kpi-card__label">{label}</span>
    <span className="frs-kpi-card__value">{value}</span>
  </div>
);

const chartTooltipStyle = {
  background: "#fff",
  border: "1px solid rgba(0,0,0,0.08)",
  borderRadius: 6,
  fontSize: 12,
  direction: "rtl",
};

const FinanceReportSection = ({
  clinicId,
  monthKey,
  year,
  month,
  report,
  reportsList,
  scopeTherapistId,
  canExportData,
}) => {
  const [sortField, setSortField] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  // ─── Scoped slices ───
  const fin = useMemo(() => {
    if (!report) return null;
    if (scopeTherapistId) {
      return report.perTherapist?.[scopeTherapistId]?.financeAnalytics || null;
    }
    return report.financeAnalytics || null;
  }, [report, scopeTherapistId]);

  const growth = useMemo(() => {
    if (!report) return null;
    if (scopeTherapistId) {
      return report.perTherapist?.[scopeTherapistId]?.growthInsights || null;
    }
    return report.growthInsights || null;
  }, [report, scopeTherapistId]);

  // outstanding[] lives only on the root doc — client-filter when scoped
  const outstanding = useMemo(() => {
    const all = report?.financeAnalytics?.outstanding || [];
    if (!scopeTherapistId) return all;
    return all.filter((o) => o.therapistId === scopeTherapistId);
  }, [report, scopeTherapistId]);

  // ─── KPIs ───
  const kpi = useMemo(() => {
    const collected = fin?.collectedTotal || 0;
    const count = fin?.collectedCount || 0;
    const expected = fin?.expectedTotal || 0;
    return {
      collected,
      count,
      avg: count > 0 ? Math.round(collected / count) : 0,
      rate: expected > 0 ? Math.round((collected / expected) * 100) : 0,
      pending: fin?.pendingTotal || 0,
      mom: growth?.momRevenuePct ?? null,
      missingPrice: fin?.missingPriceCount || 0,
    };
  }, [fin, growth]);

  // ─── Cumulative daily income ───
  const daysInMonth = useMemo(
    () => new Date(year, month, 0).getDate(),
    [year, month]
  );

  const cumulativeDaily = useMemo(() => {
    const perDay = new Array(daysInMonth + 1).fill(0);
    for (const entry of fin?.dailyCollected || []) {
      const d = Number(entry.day);
      if (d >= 1 && d <= daysInMonth) perDay[d] += entry.total || 0;
    }
    const expectedPerDay = (fin?.expectedTotal || 0) / daysInMonth;
    let cumulative = 0;
    let expectedCumulative = 0;
    return Array.from({ length: daysInMonth }, (_, i) => {
      cumulative += perDay[i + 1];
      expectedCumulative += expectedPerDay;
      return {
        day: String(i + 1),
        income: cumulative,
        expected: Math.round(expectedCumulative),
      };
    });
  }, [fin, daysInMonth]);

  const hasDailyData = (fin?.dailyCollected || []).length > 0;

  // ─── Income by patient (server: top 20 + "אחרים" bucket) ───
  const incomeByPatient = fin?.incomeByPatient || [];

  const patientColor = (entry, i) =>
    entry.patientId ? PATIENT_COLORS[i % PATIENT_COLORS.length] : NEUTRAL_GRAY;

  // ─── Payment methods / status (collected-only distributions) ───
  const methodDistribution = fin?.paymentMethodDistribution || [];
  const statusDistribution = fin?.statusDistribution || [];

  // ─── Month-over-month comparison (up to 12 months, list is newest-first) ───
  const monthsComparison = useMemo(() => {
    if (!reportsList || reportsList.length === 0) return [];
    return reportsList
      .slice(0, 12)
      .slice()
      .reverse()
      .map((r) => {
        const src = scopeTherapistId
          ? r.perTherapistTotals?.[scopeTherapistId] || {}
          : r;
        return {
          name: new Date(Date.UTC(r.year, r.month - 1, 1, 12)).toLocaleDateString(
            "he-IL",
            { month: "short", year: "2-digit", timeZone: "UTC" }
          ),
          collected: src.collectedTotal || 0,
          expected: src.expectedTotal || 0,
        };
      });
  }, [reportsList, scopeTherapistId]);

  // ─── Transactions table ───
  const rangeStart = useMemo(() => {
    const metaStart = toDate(report?.meta?.monthRangeStart);
    return (metaStart || new Date(year, month - 1, 1)).toISOString();
  }, [report, year, month]);

  const rangeEnd = useMemo(() => {
    const metaEnd = toDate(report?.meta?.monthRangeEnd);
    return (metaEnd || new Date(year, month, 0, 23, 59, 59, 999)).toISOString();
  }, [report, year, month]);

  const { data: transactions = [], isLoading: txLoading } =
    useGetTransactionsInRangeQuery(
      {
        clinicId,
        start: rangeStart,
        end: rangeEnd,
        therapistId: scopeTherapistId || undefined,
      },
      { skip: !clinicId || !year || !month }
    );

  const sortedTransactions = useMemo(() => {
    const rows = [...transactions];
    rows.sort((a, b) => {
      switch (sortField) {
        case "date": {
          const va = toDate(a.actualPaymentDate || a.expectedPaymentDate || a.createdAt)?.getTime() || 0;
          const vb = toDate(b.actualPaymentDate || b.expectedPaymentDate || b.createdAt)?.getTime() || 0;
          return sortDir === "asc" ? va - vb : vb - va;
        }
        case "amount": {
          const va = a.amount || 0;
          const vb = b.amount || 0;
          return sortDir === "asc" ? va - vb : vb - va;
        }
        case "name": {
          const va = a.patientName || a.description || "";
          const vb = b.patientName || b.description || "";
          return sortDir === "asc"
            ? va.localeCompare(vb, "he")
            : vb.localeCompare(va, "he");
        }
        case "status": {
          const va = STATUS_LABELS[a.status] || a.status || "";
          const vb = STATUS_LABELS[b.status] || b.status || "";
          return sortDir === "asc"
            ? va.localeCompare(vb, "he")
            : vb.localeCompare(va, "he");
        }
        case "method": {
          const va = PAYMENT_METHOD_LABELS[a.paymentMethod] || "";
          const vb = PAYMENT_METHOD_LABELS[b.paymentMethod] || "";
          return sortDir === "asc"
            ? va.localeCompare(vb, "he")
            : vb.localeCompare(va, "he");
        }
        default:
          return 0;
      }
    });
    return rows;
  }, [transactions, sortField, sortDir]);

  const handleSort = useCallback(
    (field) => {
      if (sortField === field) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortField(field);
        setSortDir("desc");
      }
    },
    [sortField]
  );

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === "asc" ? (
      <SortAscending size={14} weight="bold" />
    ) : (
      <SortDescending size={14} weight="bold" />
    );
  };

  // ─── CSV export (UTF-8 BOM for Hebrew Excel compatibility) ───
  const handleExportCSV = useCallback(() => {
    try {
      const BOM = "\uFEFF";
      const headers = ["תאריך", "שם", "סכום", "סטטוס", "אמצעי תשלום"];
      const rows = sortedTransactions.map((tx) =>
        [
          formatTxDate(tx.actualPaymentDate || tx.expectedPaymentDate || tx.createdAt),
          tx.patientName || tx.description || "—",
          tx.amount || 0,
          STATUS_LABELS[tx.status] || tx.status || "—",
          PAYMENT_METHOD_LABELS[tx.paymentMethod] || "לא צוין",
        ].join(",")
      );
      const csv = BOM + [headers.join(","), ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `דוח_כספים_${monthKey}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("[CK-3711] Error exporting finance report CSV:", {
        errorCode: "CK-3711",
        errorMessage: error.message,
        monthKey,
        rowCount: sortedTransactions.length,
        timestamp: new Date().toISOString(),
      });
    }
  }, [sortedTransactions, monthKey]);

  if (!fin) {
    return (
      <div className="frs-empty">
        <Wallet size={24} weight="fill" />
        <span>אין נתונים פיננסיים לחודש זה</span>
      </div>
    );
  }

  return (
    <div className="frs-section">
      {/* 1. KPI row */}
      <div className="frs-kpi-grid">
        <KpiCard
          icon={<Wallet size={26} weight="light" />}
          label="נגבה בפועל"
          value={formatCurrency(kpi.collected)}
        />
        <KpiCard
          icon={<Receipt size={26} weight="light" />}
          label="מספר תשלומים"
          value={kpi.count}
        />
        <KpiCard
          icon={<ChartLineUp size={26} weight="light" />}
          label="ממוצע לתשלום"
          value={formatCurrency(kpi.avg)}
        />
        <KpiCard
          icon={<Percent size={26} weight="light" />}
          label="אחוז גבייה"
          value={`${kpi.rate}%`}
        />
        <KpiCard
          icon={<HourglassMedium size={26} weight="light" />}
          label="ממתין לתשלום"
          value={formatCurrency(kpi.pending)}
        />
        <KpiCard
          icon={
            kpi.mom === null || kpi.mom >= 0 ? (
              <TrendUp size={26} weight="light" />
            ) : (
              <TrendDown size={26} weight="light" />
            )
          }
          label="שינוי מחודש קודם"
          value={
            kpi.mom === null ? "—" : `${kpi.mom >= 0 ? "+" : ""}${kpi.mom}%`
          }
          tone={kpi.mom === null ? undefined : kpi.mom >= 0 ? "positive" : "negative"}
        />
        <KpiCard
          icon={<Warning size={26} weight="light" />}
          label="פגישות ללא מחיר"
          value={kpi.missingPrice}
          tone={kpi.missingPrice > 0 ? "warning" : undefined}
        />
      </div>

      {/* 2. Cumulative daily income */}
      <div className="frs-chart-card frs-chart-card--full">
        <h3>מגמת הכנסות יומית (מצטבר)</h3>
        {!hasDailyData ? (
          <div className="frs-chart-empty">אין נתוני גבייה לחודש זה</div>
        ) : (
          <div className="frs-chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={cumulativeDaily}
                margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `₪${(v / 1000).toFixed(0)}k`}
                  orientation="left"
                />
                <Tooltip
                  formatter={(value, name) => [
                    formatCurrency(value),
                    name === "income" ? "הכנסה מצטברת" : "צפי מצטבר",
                  ]}
                  labelFormatter={(label) => `יום ${label}`}
                  contentStyle={chartTooltipStyle}
                />
                <Legend
                  wrapperStyle={{ fontSize: 12 }}
                  formatter={(value) =>
                    value === "income" ? "הכנסה מצטברת" : "צפי מצטבר"
                  }
                />
                <Line
                  type="monotone"
                  dataKey="income"
                  name="income"
                  stroke="#4dd9b4"
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 5 }}
                />
                <Line
                  type="monotone"
                  dataKey="expected"
                  name="expected"
                  stroke="#9ca3af"
                  strokeWidth={1.5}
                  strokeDasharray="6 4"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* 3+4. Income by patient / payment methods */}
      <div className="frs-charts-row">
        <div className="frs-chart-card">
          <h3>
            <UserCircle size={20} weight="fill" />
            הכנסות לפי מטופלים
          </h3>
          {incomeByPatient.length === 0 ? (
            <div className="frs-chart-empty">אין נתונים להצגה</div>
          ) : (
            <>
              <div className="frs-chart-container frs-chart-container--pie">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={incomeByPatient}
                      dataKey="total"
                      nameKey="patientName"
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={100}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {incomeByPatient.map((entry, i) => (
                        <Cell
                          key={entry.patientId || "others"}
                          fill={patientColor(entry, i)}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={chartTooltipStyle}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="frs-legend">
                {incomeByPatient.map((p, i) => (
                  <div
                    key={p.patientId || "others"}
                    className="frs-legend__item"
                  >
                    <span
                      className="frs-legend__dot"
                      style={{ background: patientColor(p, i) }}
                    />
                    <span className="frs-legend__name">
                      {p.patientName || "אחרים"}
                    </span>
                    <span className="frs-legend__amount">
                      {formatCurrency(p.total)}
                    </span>
                    <span className="frs-legend__count">({p.count})</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="frs-chart-card">
          <h3>חלוקה לפי אמצעי תשלום</h3>
          {methodDistribution.length === 0 ? (
            <div className="frs-chart-empty">אין נתונים להצגה</div>
          ) : (
            <>
              <div className="frs-chart-container frs-chart-container--pie">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={methodDistribution}
                      dataKey="total"
                      nameKey="method"
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={100}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {methodDistribution.map((entry) => (
                        <Cell
                          key={entry.method}
                          fill={
                            PAYMENT_METHOD_COLORS[entry.method] || NEUTRAL_GRAY
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [
                        formatCurrency(value),
                        PAYMENT_METHOD_LABELS[name] || name,
                      ]}
                      contentStyle={chartTooltipStyle}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="frs-legend">
                {methodDistribution.map((m) => (
                  <div key={m.method} className="frs-legend__item">
                    <span
                      className="frs-legend__dot"
                      style={{
                        background:
                          PAYMENT_METHOD_COLORS[m.method] || NEUTRAL_GRAY,
                      }}
                    />
                    <span className="frs-legend__name">
                      {PAYMENT_METHOD_LABELS[m.method] || m.method}
                    </span>
                    <span className="frs-legend__amount">
                      {formatCurrency(m.total)}
                    </span>
                    <span className="frs-legend__count">({m.count})</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* 5+6. Months comparison / status distribution */}
      <div className="frs-charts-row">
        <div className="frs-chart-card">
          <h3>השוואת חודשים — נגבה מול צפוי</h3>
          {monthsComparison.length <= 1 ? (
            <div className="frs-chart-empty">אין מספיק חודשים להשוואה</div>
          ) : (
            <div className="frs-chart-container">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={monthsComparison}
                  margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => `₪${(v / 1000).toFixed(0)}k`}
                    orientation="right"
                  />
                  <Tooltip
                    formatter={(value, name) => [
                      formatCurrency(value),
                      name === "collected" ? "נגבה בפועל" : "צפי הכנסה",
                    ]}
                    contentStyle={chartTooltipStyle}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    formatter={(value) =>
                      value === "collected" ? "נגבה בפועל" : "צפי הכנסה"
                    }
                  />
                  <Bar
                    dataKey="collected"
                    name="collected"
                    fill="#4dd9b4"
                    radius={[4, 4, 0, 0]}
                    barSize={18}
                  />
                  <Bar
                    dataKey="expected"
                    name="expected"
                    fill="#e5e7eb"
                    radius={[4, 4, 0, 0]}
                    barSize={18}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="frs-chart-card">
          <h3>התפלגות סטטוסים</h3>
          {statusDistribution.length === 0 ? (
            <div className="frs-chart-empty">אין נתונים להצגה</div>
          ) : (
            <>
              <div className="frs-chart-container frs-chart-container--pie">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={statusDistribution}
                      dataKey="total"
                      nameKey="status"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {statusDistribution.map((entry) => (
                        <Cell
                          key={entry.status}
                          fill={STATUS_COLORS[entry.status] || NEUTRAL_GRAY}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [
                        formatCurrency(value),
                        STATUS_LABELS[name] || name,
                      ]}
                      contentStyle={chartTooltipStyle}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="frs-status-legend">
                {statusDistribution.map((s) => (
                  <div key={s.status} className="frs-status-legend__item">
                    <span
                      className="frs-legend__dot"
                      style={{
                        background: STATUS_COLORS[s.status] || NEUTRAL_GRAY,
                      }}
                    />
                    <span>{STATUS_LABELS[s.status] || s.status}</span>
                    <span className="frs-status-legend__val">
                      {formatCurrency(s.total)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* 7. Outstanding debts */}
      {outstanding.length > 0 && (
        <div className="frs-table-card">
          <div className="frs-table-header">
            <h3>חובות פתוחים</h3>
          </div>
          <div className="frs-outstanding-list">
            {outstanding.map((o) => (
              <div className="frs-outstanding-item" key={o.transactionId}>
                <span className="frs-outstanding-item__name">
                  {o.patientName || "—"}
                </span>
                <span className="frs-outstanding-item__amount">
                  {formatCurrency(o.amount)}
                </span>
                <span className="frs-outstanding-item__date">
                  {formatTxDate(o.dueDate)}
                  {o.daysOverdue > 0 && (
                    <span className="frs-outstanding-item__overdue">
                      {" "}
                      ({o.daysOverdue} ימים באיחור)
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
          {report?.financeAnalytics?.outstandingTruncated && (
            <div className="frs-truncated-note">מוצגים החובות האחרונים בלבד</div>
          )}
        </div>
      )}

      {/* 8. Transactions table */}
      <div className="frs-table-card">
        <div className="frs-table-header">
          <h3>פירוט תנועות החודש</h3>
          {canExportData && sortedTransactions.length > 0 && (
            <button
              type="button"
              className="frs-export-btn"
              onClick={handleExportCSV}
            >
              <Export size={18} weight="bold" />
              ייצוא CSV
            </button>
          )}
        </div>

        {txLoading ? (
          <ContentLoader message="טוען תנועות..." />
        ) : sortedTransactions.length === 0 ? (
          <div className="frs-table-empty">אין תנועות להצגה בחודש זה</div>
        ) : (
          <>
            <div className="frs-table-wrapper">
              <table className="frs-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort("date")}>
                      תאריך <SortIcon field="date" />
                    </th>
                    <th onClick={() => handleSort("name")}>
                      מטופל/ת <SortIcon field="name" />
                    </th>
                    <th onClick={() => handleSort("amount")}>
                      סכום <SortIcon field="amount" />
                    </th>
                    <th onClick={() => handleSort("status")}>
                      סטטוס <SortIcon field="status" />
                    </th>
                    <th onClick={() => handleSort("method")}>
                      אמצעי תשלום <SortIcon field="method" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTransactions.map((tx) => (
                    <tr key={tx.id}>
                      <td>
                        {formatTxDate(
                          tx.actualPaymentDate ||
                            tx.expectedPaymentDate ||
                            tx.createdAt
                        )}
                      </td>
                      <td className="frs-table__name">
                        {tx.patientName || tx.description || "—"}
                      </td>
                      <td className="frs-table__amount">
                        {formatCurrency(tx.amount)}
                      </td>
                      <td>
                        <span
                          className="frs-table__status-badge"
                          style={{
                            background: `${STATUS_COLORS[tx.status] || NEUTRAL_GRAY}18`,
                            color: STATUS_COLORS[tx.status] || NEUTRAL_GRAY,
                          }}
                        >
                          {STATUS_LABELS[tx.status] || tx.status || "—"}
                        </span>
                      </td>
                      <td>
                        <span
                          className="frs-table__method-dot"
                          style={{
                            background:
                              PAYMENT_METHOD_COLORS[tx.paymentMethod] ||
                              NEUTRAL_GRAY,
                          }}
                        />
                        {PAYMENT_METHOD_LABELS[tx.paymentMethod] || "לא צוין"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="frs-table-footer">
              <span>{sortedTransactions.length} רשומות</span>
              <span className="frs-table-footer__total">
                סה"כ:{" "}
                {formatCurrency(
                  sortedTransactions.reduce((s, t) => s + (t.amount || 0), 0)
                )}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FinanceReportSection;
